import './applicantAddCoapplicantPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Disclosure } from '../../../components/Disclosure/Disclosure';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    addCoapplicant,
    disableCoapplicantFailedMessage,
    isDisablingCoapplicant,
    disableCoapplicant,
    setAddCoapplicant,
  } = useApplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      addCoApplicantValue: addCoapplicant,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    setIsInitialSelection(isEmpty(addCoapplicant) && isValidClickEvent(event));
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { addCoApplicantValue } = values;

    if (!addCoApplicantValue) {
      return;
    }

    setAddCoapplicant(addCoApplicantValue);

    if (addCoApplicantValue === NO) {
      const disabledCoapplicant = await disableCoapplicant();

      if (disabledCoapplicant) {
        navigate(next());

        return;
      }
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page applicant-add-coapplicant-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantAddCoapplicant.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantAddCoapplicant.form.h1}>
                {t('ApplicantAddCoapplicantPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantAddCoapplicant.form.h2}>
                {t('ApplicantAddCoapplicantPage.subtitle')}
              </h2>

              {!isEmpty(disableCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantAddCoapplicant.alert.error
                    }
                    severity="error"
                  >
                    {disableCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="addCoApplicantValue"
                  options={[
                    {
                      label: t('ApplicantAddCoapplicantPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.applicantAddCoapplicant.form.radioOption1,
                    },
                    {
                      label: t('ApplicantAddCoapplicantPage.no'),
                      value: NO,
                      testId:
                        locator.pages.applicantAddCoapplicant.form.radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantAddCoapplicant.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantAddCoapplicant.form.submitButton
                  }
                  disabled={isEmpty(addCoapplicant)}
                  loader={isDisablingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('ApplicantAddCoapplicantPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={
                  locator.pages.applicantAddCoapplicant.form
                    .coapplicantDisclosure
                }
              >
                <Disclosure i18nKey="disclosures.jointCredit" />
              </p>
            </div>
            <footer
              data-testid={locator.pages.applicantAddCoapplicant.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantAddCoapplicantPage';
